import type {
  ChatMessage,
  ChatRoomDetail,
  ChatRoomSummary,
} from '@src/services/chat';

/**
 * 채팅 로컬 테스트용 더미 데이터/로직 (백엔드 없이 UI 확인).
 * NEXT_PUBLIC_CHAT_MOCK=true 일 때 chat.ts가 이 구현으로 위임한다.
 * 메모리 상태라 새로고침하면 초기화된다.
 */

export const MOCK_ME = 'me-uuid';

let seq = 1000;
const nextId = () => (seq += 1);
const isoAgo = (min: number) =>
  new Date(Date.now() - min * 60000).toISOString();

type Room = ChatRoomDetail & {
  lastMessageContent: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
};

const mkMsg = (
  roomUuid: string,
  senderUuid: string,
  content: string,
  minAgo: number,
  extra?: Partial<ChatMessage>
): ChatMessage => ({
  id: nextId(),
  uuid: `msg-${seq}`,
  roomUuid,
  senderUuid,
  content,
  fileUrl: null,
  type: 'TEXT',
  createdAt: isoAgo(minAgo),
  parentMessageId: null,
  parentSenderUuid: null,
  parentContent: null,
  ...extra,
});

// 방
const rooms: Room[] = [
  {
    uuid: 'room-1',
    opponentUuid: 'op-1',
    opponentName: '김하늘',
    opponentImage: null,
    donationUuid: 'don-1',
    donationTitle: '서울성모병원 O형 혈소판 3팩 급구',
    opponentRole: '수혈 요청',
    donationBloodType: 'O형 Rh+',
    donationRegion: '서울 서초구',
    createdAt: isoAgo(2880),
    opponentLastReadAt: isoAgo(20),
    lastMessageContent: '정말 감사합니다! 내일 오전에 뵐게요',
    lastMessageAt: isoAgo(4),
    unreadCount: 2,
  },
  {
    uuid: 'room-2',
    opponentUuid: 'op-2',
    opponentName: '이준서',
    opponentImage: null,
    donationUuid: 'don-2',
    donationTitle: '강남 헌혈의집 A형 지정헌혈',
    opponentRole: '헌혈자',
    donationBloodType: 'A형 Rh+',
    donationRegion: '서울 강남구',
    createdAt: isoAgo(5000),
    opponentLastReadAt: isoAgo(60),
    lastMessageContent: '강남 헌혈의집으로 예약 잡았어요',
    lastMessageAt: isoAgo(65),
    unreadCount: 1,
  },
  {
    uuid: 'room-3',
    opponentUuid: 'op-3',
    opponentName: '박서연',
    opponentImage: null,
    donationUuid: 'don-3',
    donationTitle: 'B형 혈소판 요청',
    opponentRole: '수혈 요청',
    donationBloodType: 'B형 Rh+',
    donationRegion: '경기 성남시',
    createdAt: isoAgo(20000),
    opponentLastReadAt: isoAgo(1000),
    lastMessageContent: '덕분에 수술 잘 끝났습니다 :)',
    lastMessageAt: isoAgo(1500),
    unreadCount: 0,
  },
];

// 방별 메시지
const messages: Record<string, ChatMessage[]> = {
  'room-1': [
    mkMsg(
      'room-1',
      'SYSTEM',
      '수혈 요청 게시글을 통해 시작된 대화예요. 피플은 금전 거래를 중개하지 않아요.',
      180,
      { type: 'SYSTEM' }
    ),
    mkMsg(
      'room-1',
      'op-1',
      '안녕하세요, 어머니 수술로 O형 혈소판이 급하게 필요해서 연락드렸어요.',
      60
    ),
    mkMsg(
      'room-1',
      MOCK_ME,
      '안녕하세요! 지정헌혈 가능합니다. 어느 병원으로 가면 될까요?',
      55
    ),
    mkMsg(
      'room-1',
      'op-1',
      '서울성모병원이에요. 8월 14일 오전까지 도착하면 됩니다.',
      40
    ),
    mkMsg('room-1', MOCK_ME, '네, 14일 오전 10시 30분에 방문할게요.', 30, {
      parentMessageId: 0,
      parentSenderUuid: 'op-1',
      parentContent: '서울성모병원이에요. 8월 14일 오전까지 도착하면…',
    }),
    mkMsg('room-1', 'op-1', '정말 감사합니다! 내일 오전에 뵐게요', 4),
  ],
  'room-2': [
    mkMsg('room-2', 'SYSTEM', '헌혈의집 지정헌혈 대화가 시작되었어요.', 200, {
      type: 'SYSTEM',
    }),
    mkMsg('room-2', 'op-2', '강남 헌혈의집으로 예약 잡았어요', 65),
  ],
  'room-3': [mkMsg('room-3', 'op-3', '덕분에 수술 잘 끝났습니다 :)', 1500)],
};

const toSummary = (r: Room): ChatRoomSummary => ({
  uuid: r.uuid,
  opponentUuid: r.opponentUuid,
  opponentName: r.opponentName,
  opponentImage: r.opponentImage,
  opponentRole: r.opponentRole,
  donationBloodType: r.donationBloodType,
  donationUuid: r.donationUuid,
  donationTitle: r.donationTitle,
  lastMessageContent: r.lastMessageContent,
  lastMessageAt: r.lastMessageAt,
  unreadCount: r.unreadCount,
});

const findRoom = (uuid: string) => rooms.find((r) => r.uuid === uuid);

const delay = <T>(v: T, ms = 200): Promise<T> =>
  new Promise((res) => setTimeout(() => res(v), ms));

const chatMock = {
  async createOrGetRoom(donationUuid: string): Promise<ChatRoomDetail> {
    const room = rooms.find((r) => r.donationUuid === donationUuid) ?? rooms[0];
    return delay({ ...room });
  },
  async getRooms(): Promise<ChatRoomSummary[]> {
    return delay(rooms.map(toSummary));
  },
  async getRoom(roomUuid: string): Promise<ChatRoomDetail> {
    const r = findRoom(roomUuid) ?? rooms[0];
    // 데모: 상대가 방금 읽은 것처럼 갱신 → 내 메시지에 "읽음" 표시가 나타남
    r.opponentLastReadAt = new Date().toISOString();
    return delay({ ...r });
  },
  async getMessages(
    roomUuid: string,
    opts?: { afterId?: number; beforeId?: number; size?: number }
  ): Promise<ChatMessage[]> {
    const all = messages[roomUuid] ?? [];
    if (opts?.afterId != null) {
      return delay(
        all.filter((m) => m.id > (opts.afterId as number)),
        100
      );
    }
    return delay([...all]);
  },
  async sendMessage(
    roomUuid: string,
    content: string,
    parentMessageId?: number | null
  ): Promise<ChatMessage> {
    const all = messages[roomUuid] ?? (messages[roomUuid] = []);
    const parent = parentMessageId
      ? all.find((m) => m.id === parentMessageId)
      : null;
    const msg = mkMsg(roomUuid, MOCK_ME, content, 0, {
      parentMessageId: parent ? parent.id : null,
      parentSenderUuid: parent ? parent.senderUuid : null,
      parentContent: parent ? parent.content.slice(0, 40) : null,
    });
    all.push(msg);
    const r = findRoom(roomUuid);
    if (r) {
      r.lastMessageContent = content;
      r.lastMessageAt = msg.createdAt;
    }
    return delay(msg, 120);
  },
  async sendImage(roomUuid: string, file: File): Promise<ChatMessage> {
    const all = messages[roomUuid] ?? (messages[roomUuid] = []);
    const msg = mkMsg(roomUuid, MOCK_ME, '사진을 보냈습니다.', 0, {
      type: 'IMAGE',
      fileUrl: URL.createObjectURL(file), // 로컬에서 고른 이미지가 그대로 보임
    });
    all.push(msg);
    return delay(msg, 120);
  },
  async reportRoom(): Promise<void> {
    return delay(undefined);
  },
  async blockOpponent(): Promise<void> {
    return delay(undefined);
  },
  async markRead(roomUuid: string): Promise<void> {
    const r = findRoom(roomUuid);
    if (r) r.unreadCount = 0;
    return delay(undefined, 0);
  },
  async getUnreadCount(): Promise<number> {
    return delay(rooms.reduce((s, r) => s + r.unreadCount, 0));
  },
};

export default chatMock;
